/**
 * Error Boundary Component
 * Catches and handles React component errors gracefully
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';
import { styleDefinitions } from '../../theme/styles';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  level?: 'page' | 'section' | 'component';
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// Get styles at module level for class component
const styles = styleDefinitions;

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Call optional error handler
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Log to external service (e.g., Sentry, LogRocket)
    this.logErrorToService(error, errorInfo);
  }

  logErrorToService(error: Error, errorInfo: ErrorInfo) {
    // Implement error logging to external service
    // For now, just log to console
    console.group('🚨 Error Details');
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Component Stack:', errorInfo.componentStack);
    console.groupEnd();
  }

  handleReset = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { level = 'component' } = this.props;
      const isPageLevel = level === 'page';

      // Default error UI
      return (
        <div style={{
          ...styles.layout.flexCenterAll,
          minHeight: isPageLevel ? '100vh' : '400px',
          background: 'var(--color-gray-50)',
          padding: 'var(--space-4)'
        }}>
          <div style={{
            ...styles.card.elevated,
            maxWidth: '28rem',
            width: '100%',
            padding: 'var(--space-6)'
          }}>
            <div style={{
              ...styles.layout.flexCenterAll,
              width: '48px',
              height: '48px',
              margin: '0 auto',
              background: '#fee2e2',
              borderRadius: 'var(--radius-full)'
            }}>
              <svg
                style={{ width: '24px', height: '24px', color: '#dc2626' }}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            
            <h2 style={{
              ...styles.typography.h3,
              ...styles.typography.textCenter,
              marginTop: 'var(--space-4)',
              color: 'var(--color-gray-900)'
            }}>
              Something went wrong
            </h2>
            
            <p style={{
              ...styles.typography.bodySmall,
              ...styles.typography.textCenter,
              marginTop: 'var(--space-2)',
              color: 'var(--color-gray-600)'
            }}>
              An unexpected error occurred. The error has been logged and we'll look into it.
            </p>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{
                marginTop: 'var(--space-4)',
                padding: 'var(--space-3)',
                background: 'var(--color-gray-100)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-xs)'
              }}>
                <summary style={{
                  cursor: 'pointer',
                  fontWeight: 'var(--font-medium)',
                  color: 'var(--color-gray-700)'
                }}>
                  Error Details (Development Only)
                </summary>
                <div style={{
                  marginTop: 'var(--space-2)',
                  gap: 'var(--space-1)'
                }}>
                  <p style={{
                    color: '#dc2626',
                    fontFamily: 'monospace'
                  }}>
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <pre style={{
                      marginTop: 'var(--space-2)',
                      color: 'var(--color-gray-600)',
                      overflow: 'auto',
                      maxHeight: '160px'
                    }}>
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            <div style={{
              ...styles.layout.flex,
              gap: 'var(--space-3)',
              marginTop: 'var(--space-6)'
            }}>
              <button
                onClick={this.handleReset}
                style={{
                  ...styles.button.base,
                  ...styles.button.primary,
                  ...styles.button.medium,
                  flex: 1
                }}
              >
                Try Again
              </button>
              <button
                onClick={() => window.location.href = '/'}
                style={{
                  ...styles.button.base,
                  ...styles.button.secondary,
                  ...styles.button.medium,
                  flex: 1
                }}
              >
                Go Home
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * HOC for wrapping components with error boundary
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode,
  onError?: (error: Error, errorInfo: ErrorInfo) => void
) {
  return (props: P) => (
    <ErrorBoundary fallback={fallback} onError={onError}>
      <Component {...props} />
    </ErrorBoundary>
  );
}